import {
  MIN_PER_HOUR,
  SEC_PER_MIN,
  SUPPORTED_UNITS,
} from '@/helpers/unit-formatter/unit-formatter.constants';
import { UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import { durationToSeconds, fpDivide } from '@/helpers/unit-formatter/unit-formatter.utils';

/**
 * Transforms duration values from H:MM format to time units.
 * Converts duration/timer strings to seconds, minutes, or hours.
 *
 * @param {UnitValue} unitValue - The duration value in H:MM format
 * @param {any} unitValue.value - The duration string (e.g., "1:30", "0:05")
 * @param {string} [unitValue.units] - The current units (should be 'duration' or 'timer')
 * @param {string} [newUnits] - Target time unit ('sec', 'min', 'hrs')
 * @returns {UnitValue} Transformed time value in the target units
 *   - Returns original value if newUnits is not recognized
 *   - Returns original value if input value is null/undefined or invalid format
 *
 * @example
 * transformDuration({ value: '1:30', units: 'duration' }, 'sec')
 * // Returns: { value: 5400, units: 'sec' }
 *
 * @example
 * transformDuration({ value: '1:30', units: 'duration' }, 'min')
 * // Returns: { value: 90, units: 'min' }
 *
 * @example
 * transformDuration({ value: '1:30', units: 'duration' }, 'hrs')
 * // Returns: { value: 1.5, units: 'hrs' }
 */
export const transformDuration = (unitValue: UnitValue, newUnits?: string): UnitValue => {
  const seconds = durationToSeconds(unitValue.value);
  if (seconds == null) return unitValue;

  switch (newUnits?.toLowerCase()) {
    case SUPPORTED_UNITS.SEC:
      return {
        value: seconds,
        units: newUnits,
      };
    case SUPPORTED_UNITS.MIN:
      return {
        value: fpDivide(seconds, SEC_PER_MIN),
        units: newUnits,
      };
    case SUPPORTED_UNITS.HRS:
      return {
        value: fpDivide(seconds, SEC_PER_MIN * MIN_PER_HOUR),
        units: newUnits,
      };
    default:
      return unitValue;
  }
};
